/* eslint-disable complexity */
/**
 * UserSessionReducer
 */

import { IAction } from '..';
import {
  SessionActions,
  XprtDataActions,
  APPLICATION_STARTUP_SUCCESS,
} from '@store/actions';
import { IPerson } from '@shared/types';
import { XprtSessionService } from '@shared/services';
import { get } from 'lodash';
import { Reducer } from 'redux';
import { DEFAULT_LOCALE } from '@config/global-constants';
import { updateUserDefineStringLocale } from './session.utils';
import { IUserSession } from './session.types';

const INITIAL_STATE: IUserSession = {
  accessToken: null,
  personalToken: null,
  errors: null,
  user: null,
  companies: [],
  companyRoles: {},
  showCompanySelector: false,
  currentCompanyId: null,
  isIframeMode: false,
  applicationStarted: false,
  i18n: {},
};
/**
 * This function updates user session state
 *
 */
export const userSessionReducer: Reducer<IUserSession> = (
  state: IUserSession = INITIAL_STATE,
  action: IAction,
): IUserSession => {
  const { payload } = action;
  switch (action.type) {
    case APPLICATION_STARTUP_SUCCESS:
      return Object.assign({}, state, { applicationStarted: true });
    case SessionActions.RESTORE_TOKENS_SUCCESS:
      return Object.assign({}, state, {
        errors: [],
        accessToken: payload.accessToken,
        personalToken: get(
          XprtSessionService.parseToken(payload.personalToken),
          'token',
          null,
        ),
        currentCompanyId: get(
          XprtSessionService.parseToken(payload.accessToken),
          'user.company_id',
          null,
        ),
      });
    case SessionActions.FETCH_ACCESS_TOKEN_SUCCESS:
      return Object.assign({}, state, {
        errors: [],
        accessToken: payload,
        currentCompanyId: get(
          XprtSessionService.parseToken(payload),
          'user.company_id',
          null,
        ),
      });
    case SessionActions.FETCH_USER_INFO_SUCCESS:
      const userAfterLogin: IPerson = Object.assign({}, state.user, {
        name: payload.user.name,
        id: payload.user.id,
        email: payload.user.email,
      });

      return Object.assign({}, state, {
        errors: [],
        user: userAfterLogin,
      });
    case SessionActions.FETCH_PERSONAL_TOKEN_ERROR:
      return Object.assign({}, state, {
        errors: payload.errors,
      });

    case XprtDataActions.FETCH_COMPANIES:
      return Object.assign({}, state, {
        companies: [],
      });

    case XprtDataActions.FETCH_COMPANIES_SUCCESS:
      return Object.assign({}, state, {
        companies: payload.items,
      });
    case XprtDataActions.SHOW_COMPANY_SELECTOR:
      return Object.assign({}, state, {
        showCompanySelector: true,
      });
    case XprtDataActions.HIDE_COMPANY_SELECTOR:
      return Object.assign({}, state, {
        showCompanySelector: false,
      });
    case SessionActions.SET_IFRAME_MODE:
      return Object.assign({}, state, {
        isIframeMode: payload,
      });

    case XprtDataActions.FETCH_COMPANIES_ERROR:
    case SessionActions.FETCH_ACCESS_TOKEN_ERROR:
    case SessionActions.FETCH_PERMISSIONS_ERROR:
      return Object.assign({}, state, {
        errors: payload,
      });
    case SessionActions.FETCH_PERSONAL_TOKEN_SUCCESS:
      const token = get(XprtSessionService.parseToken(payload), 'token', null);

      return Object.assign({}, state, {
        personalToken: token,
        currentCompanyId: get(
          XprtSessionService.parseToken(payload),
          'user.company_id',
          null,
        ),
      });

    case SessionActions.RESTORE_PERSONAL_TOKEN_SUCCESS:
      return Object.assign({}, state, {
        personalToken: get(
          XprtSessionService.parseToken(payload),
          'token',
          null,
        ),
      });

    case SessionActions.FETCH_PERMISSIONS:
      return Object.assign({}, state, {
        userPermissions: {},
      });

    case SessionActions.FETCH_PERMISSIONS_SUCCESS:
      return Object.assign({}, state, {
        userPermissions: {
          total: payload.total,
          items: payload.items,
        },
      });

    case SessionActions.LOGOUT_USER:
      /**
       * LOGOUT_USER can be dispatched after TOKEN_RETRIEVED, we have to keep errors
       * just to show them on login page.
       */
      return Object.assign({}, INITIAL_STATE, {
        errors: state.errors,
        userPermissions: { total: 0, items: [] },
        accessToken: null,
        personalToken: null,
      });
    case SessionActions.SAVE_LOCALES:
      return updateUserDefineStringLocale(
        Object.assign({}, state, {
          i18n: {
            ...state.i18n,
            locales: {
              ...payload.locales,
              default: DEFAULT_LOCALE,
            },
          },
        }),
      );
    case SessionActions.SET_TRANSLATION_INITIALIZED:
      return Object.assign({}, state, {
        i18n: {
          ...state.i18n,
          translationInitialized: payload?.isInitialized ?? false,
        },
      });
    default:
      return state;
  }
};
